import { ChevronDown, ChevronRight } from 'lucide-react'
import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { NavLink, useLocation } from 'react-router-dom'

import type { AdminMenuNode } from '@/api/menu'
import { menuFaToLucide } from '@/lib/menu-icon'
import { useT } from '@/lib/i18n/AdminI18nProvider'
import { localizeMenuLabel } from '@/lib/i18n/menu-labels'
import { cn } from '@/lib/utils'

function splitMenuPath(itemPath: string): { pathname: string; search: string } {
  const q = itemPath.indexOf('?')
  if (q === -1) {
    return { pathname: itemPath, search: '' }
  }

  return { pathname: itemPath.slice(0, q), search: itemPath.slice(q) }
}

function menuPathToTo(itemPath: string): string | { pathname: string; search: string } {
  const { pathname, search } = splitMenuPath(itemPath)
  return search ? { pathname, search } : pathname
}

function searchParamsMatch(currentSearch: string, targetSearch: string): boolean {
  const current = new URLSearchParams(currentSearch)
  const target = new URLSearchParams(targetSearch)
  const targetKeys = [...target.keys()]

  if (targetKeys.length === 0) {
    return [...current.keys()].length === 0
  }

  return targetKeys.every((key) => current.get(key) === target.get(key))
}

function pathMatches(
  pathname: string,
  search: string,
  itemPath: string,
  allMenuPathnames?: string[],
): boolean {
  const target = splitMenuPath(itemPath)

  if (target.pathname === '/') {
    return pathname === '/' && searchParamsMatch(search, target.search)
  }

  if (pathname !== target.pathname) {
    // Alt rota: /reports/stock → /reports menüsü
    if (!pathname.startsWith(`${target.pathname}/`)) {
      return false
    }
    // Aynı pathname'i daha spesifik karşılayan başka bir menü öğesi varsa
    // (ör. /themes/page-templates), üst öğe (/themes) aktif sayılmaz
    if (
      allMenuPathnames?.some(
        (p) =>
          p.length > target.pathname.length &&
          (pathname === p || pathname.startsWith(`${p}/`)),
      )
    ) {
      return false
    }
    if (target.search) {
      return searchParamsMatch(search, target.search)
    }
    return [...new URLSearchParams(search).keys()].length === 0
  }

  return searchParamsMatch(search, target.search)
}

function collectMenuPathnames(nodes: AdminMenuNode[], acc: string[] = []): string[] {
  for (const n of nodes) {
    if (n.path) {
      acc.push(splitMenuPath(n.path).pathname)
    }
    collectMenuPathnames(n.children, acc)
  }
  return acc
}

function branchContainsActiveRoute(
  node: AdminMenuNode,
  pathname: string,
  search: string,
  allMenuPathnames?: string[],
): boolean {
  if (node.path && pathMatches(pathname, search, node.path, allMenuPathnames)) {
    return true
  }

  return node.children.some((c) => branchContainsActiveRoute(c, pathname, search, allMenuPathnames))
}

function initialOpenIds(
  nodes: AdminMenuNode[],
  pathname: string,
  search: string,
  allMenuPathnames?: string[],
): Set<number> {
  const open = new Set<number>()

  function walk(list: AdminMenuNode[], ancestors: number[]): boolean {
    for (const n of list) {
      if (n.path && pathMatches(pathname, search, n.path, allMenuPathnames)) {
        ancestors.forEach((id) => open.add(id))
        return true
      }
      if (n.children.length > 0 && walk(n.children, [...ancestors, n.id])) {
        ancestors.forEach((id) => open.add(id))
        open.add(n.id)
        return true
      }
    }
    return false
  }

  walk(nodes, [])
  return open
}

function filterByEnabledModules(
  nodes: AdminMenuNode[] | null | undefined,
  enabledKeys: string[] | null,
): AdminMenuNode[] {
  if (!nodes?.length) return []
  const out: AdminMenuNode[] = []
  for (const n of nodes) {
    // Modül listesi yüklenene kadar modül bağlı menüleri gizle (yanlışlıkla gösterme)
    if (n.module_key && (enabledKeys === null || !enabledKeys.includes(n.module_key))) {
      continue
    }
    const children = filterByEnabledModules(n.children ?? [], enabledKeys)
    const hasPath = Boolean(n.path && n.path.length > 0)
    if (!hasPath && children.length === 0) {
      continue
    }
    out.push({ ...n, children })
  }
  return out
}

const rowBase =
  'relative flex w-full items-center gap-2.5 rounded-lg py-2 text-[13px] font-medium transition-all duration-200'

const flyoutLinkClass = (isActive: boolean) =>
  cn(
    'flex items-center gap-2.5 rounded-lg px-3 py-1.5 text-xs transition-colors',
    isActive
      ? 'bg-mainColor text-white shadow-soft'
      : 'text-foreground/90 hover:bg-muted/80 dark:hover:bg-mainDark/40',
  )

function FlyoutBullet() {
  return (
    <span className="grid h-4 w-4 shrink-0 place-content-center rounded-full border border-current/35" aria-hidden>
      <span className="h-1.5 w-1.5 rounded-full bg-current opacity-90" />
    </span>
  )
}

/** Flyout içi: alt dallar (CodeXpin tarzı madde işaretli liste) */
function FlyoutMenuBody({
  nodes,
  pathname,
  search,
  onPick,
  depth = 0,
  allMenuPathnames,
  labelOf,
}: {
  nodes: AdminMenuNode[]
  pathname: string
  search: string
  onPick: () => void
  depth?: number
  allMenuPathnames?: string[]
  labelOf: (node: AdminMenuNode) => string
}) {
  return (
    <ul className={cn('space-y-0.5 py-1', depth > 0 && 'mt-1 border-t border-border/50 pt-2')}>
      {nodes.map((n) => {
        const hasKids = n.children.length > 0

        if (hasKids) {
          const parentActive = n.path ? pathMatches(pathname, search, n.path, allMenuPathnames) : false
          return (
            <li key={n.id}>
              {n.path ? (
                <NavLink
                  to={menuPathToTo(n.path)}
                  end
                  onClick={onPick}
                  aria-current={parentActive ? 'page' : undefined}
                  className={cn(flyoutLinkClass(parentActive), 'mx-1')}
                >
                  <FlyoutBullet />
                  <span className="min-w-0 flex-1 truncate">{labelOf(n)}</span>
                </NavLink>
              ) : null}
              <div className={cn(n.path ? 'mt-1 pl-1' : '')}>
                {!n.path ? (
                  <p className="px-3 pb-1 pt-1 text-[10px] font-bold uppercase tracking-wide text-muted-foreground">
                    {labelOf(n)}
                  </p>
                ) : null}
                <FlyoutMenuBody
                  nodes={n.children}
                  pathname={pathname}
                  search={search}
                  onPick={onPick}
                  depth={depth + 1}
                  allMenuPathnames={allMenuPathnames}
                  labelOf={labelOf}
                />
              </div>
            </li>
          )
        }

        if (n.path) {
          const active = pathMatches(pathname, search, n.path, allMenuPathnames)
          return (
            <li key={n.id}>
              <NavLink
                to={menuPathToTo(n.path)}
                end
                onClick={onPick}
                aria-current={active ? 'page' : undefined}
                className={cn(flyoutLinkClass(active), 'mx-1')}
              >
                <FlyoutBullet />
                <span className="min-w-0 flex-1 truncate">{labelOf(n)}</span>
              </NavLink>
            </li>
          )
        }

        return null
      })}
    </ul>
  )
}

type FlyoutState = {
  id: number
  node: AdminMenuNode
  rect: DOMRect
}

type AdminSidebarNavProps = {
  menu: AdminMenuNode[]
  /** null = modüller henüz yüklenmedi; modül bağlı menüler gizlenir */
  enabledModuleKeys: string[] | null
  modulesLoading?: boolean
  collapsed: boolean
  /** true: masaüstü (md+) — alt menü sağa flyout; false: mobil drawer — accordion */
  isDesktopLayout: boolean
  onNavigate: () => void
}

export function AdminSidebarNav({
  menu,
  enabledModuleKeys,
  modulesLoading: _modulesLoading = false,
  collapsed,
  isDesktopLayout,
  onNavigate,
}: AdminSidebarNavProps) {
  const t = useT()
  const labelOf = useCallback(
    (node: AdminMenuNode) => localizeMenuLabel(node.name, node.path, t),
    [t],
  )
  const location = useLocation()
  const pathname = location.pathname
  const search = location.search

  const filteredRoots = useMemo(
    () => filterByEnabledModules(menu, enabledModuleKeys),
    [menu, enabledModuleKeys],
  )

  const allMenuPathnames = useMemo(() => collectMenuPathnames(filteredRoots), [filteredRoots])

  const [openIds, setOpenIds] = useState<Set<number>>(() =>
    initialOpenIds(filteredRoots.flatMap((s) => s.children), pathname, search, allMenuPathnames),
  )

  const [flyout, setFlyout] = useState<FlyoutState | null>(null)
  const [flyoutStyle, setFlyoutStyle] = useState<{ top: number; left: number; maxHeight: number } | null>(null)
  const closeTimer = useRef<ReturnType<typeof setTimeout> | null>(null)
  const flyoutPanelRef = useRef<HTMLDivElement>(null)

  const clearCloseTimer = useCallback(() => {
    if (closeTimer.current) {
      clearTimeout(closeTimer.current)
      closeTimer.current = null
    }
  }, [])

  const scheduleCloseFlyout = useCallback(() => {
    clearCloseTimer()
    closeTimer.current = setTimeout(() => setFlyout(null), 140)
  }, [clearCloseTimer])

  const openFlyout = useCallback(
    (node: AdminMenuNode, el: HTMLElement) => {
      if (!node.children.length) return
      clearCloseTimer()
      setFlyout({ id: node.id, node, rect: el.getBoundingClientRect() })
    },
    [clearCloseTimer],
  )

  useEffect(() => {
    setOpenIds(initialOpenIds(filteredRoots.flatMap((s) => s.children), pathname, search, allMenuPathnames))
  }, [filteredRoots, pathname, search, allMenuPathnames])

  useEffect(() => {
    setFlyout(null)
  }, [pathname])

  // Flyout'u viewport'a sığdır: alta taşarsa yukarı kaydır, hâlâ uzunsa iç scroll ver.
  useLayoutEffect(() => {
    if (!flyout || !isDesktopLayout) {
      setFlyoutStyle(null)
      return
    }

    const gap = 8
    const margin = 12
    const panelW = flyoutPanelRef.current?.offsetWidth ?? 280
    const panelH = flyoutPanelRef.current?.offsetHeight ?? 0
    const vw = window.innerWidth
    const vh = window.innerHeight
    const maxHeight = vh - margin * 2

    let left = flyout.rect.right + gap
    if (left + panelW > vw - margin) {
      left = Math.max(margin, flyout.rect.left - panelW - gap)
    }

    const effectiveH = Math.min(panelH || maxHeight, maxHeight)
    let top = flyout.rect.top - 4
    if (top + effectiveH > vh - margin) {
      top = vh - margin - effectiveH
    }
    if (top < margin) top = margin

    setFlyoutStyle({ top, left, maxHeight })
  }, [flyout, isDesktopLayout])

  useEffect(() => {
    if (!flyout) return
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setFlyout(null)
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [flyout])

  useEffect(() => {
    if (!flyout) return
    const onScroll = () => setFlyout(null)
    const nav = document.querySelector('aside nav')
    nav?.addEventListener('scroll', onScroll, { passive: true })
    window.addEventListener('scroll', onScroll, { passive: true })
    return () => {
      nav?.removeEventListener('scroll', onScroll)
      window.removeEventListener('scroll', onScroll)
    }
  }, [flyout])

  useEffect(() => {
    if (!flyout) return
    const onDown = (e: MouseEvent) => {
      const t = e.target as Node
      if (flyoutPanelRef.current?.contains(t)) return
      const triggers = document.querySelectorAll('[data-admin-nav-flyout-trigger="1"]')
      for (let i = 0; i < triggers.length; i++) {
        if (triggers[i]!.contains(t)) return
      }
      setFlyout(null)
    }
    document.addEventListener('mousedown', onDown)
    return () => document.removeEventListener('mousedown', onDown)
  }, [flyout])

  function toggleAccordion(id: number) {
    setOpenIds((prev) => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }

  const leafRowClass = (isActive: boolean, collapsedOnly: boolean) =>
    cn(
      rowBase,
      collapsedOnly ? 'mx-auto w-11 justify-center px-0' : 'px-3',
      isActive
        ? 'bg-mainColor text-white shadow-md dark:bg-mainColor dark:text-white'
        : 'text-muted-foreground hover:bg-muted/90 hover:text-foreground dark:hover:bg-mainDark/50',
      isActive && !collapsedOnly && 'border-l-2 border-white/80 pl-[calc(0.75rem-2px)]',
      isActive &&
        collapsedOnly &&
        'ring-2 ring-mainColor/45 ring-offset-2 ring-offset-card dark:ring-offset-secondaryDark',
    )

  const branchRowClass = (isActive: boolean, collapsedOnly: boolean) =>
    cn(
      rowBase,
      'text-left',
      collapsedOnly ? 'mx-auto w-11 justify-center px-0' : 'px-3',
      isActive
        ? 'bg-mainColor text-white shadow-md dark:bg-mainColor'
        : 'text-muted-foreground hover:bg-muted/90 hover:text-foreground dark:hover:bg-mainDark/50',
    )

  /** Mobil: derinlikli accordion */
  function MobileRow({ node, depth }: { node: AdminMenuNode; depth: number }) {
    const Icon = menuFaToLucide(node.icon)
    const hasKids = node.children.length > 0
    const open = openIds.has(node.id)
    const activeBranch = branchContainsActiveRoute(node, pathname, search, allMenuPathnames)
    const pl = 12 + depth * 12

    if (!hasKids && node.path) {
      const active = pathMatches(pathname, search, node.path, allMenuPathnames)
      return (
        <NavLink
          to={menuPathToTo(node.path)}
          end
          style={{ paddingLeft: pl }}
          onClick={() => onNavigate()}
          aria-current={active ? 'page' : undefined}
          className={leafRowClass(active, false)}
        >
          <Icon className="h-4 w-4 shrink-0" aria-hidden />
          <span className="min-w-0 flex-1 truncate">{labelOf(node)}</span>
        </NavLink>
      )
    }

    if (hasKids) {
      return (
        <div className="flex flex-col gap-0.5">
          <button
            type="button"
            style={{ paddingLeft: pl }}
            onClick={() => toggleAccordion(node.id)}
            className={branchRowClass(activeBranch, false)}
          >
            <Icon className="h-4 w-4 shrink-0" aria-hidden />
            <span className="min-w-0 flex-1 truncate text-left">{labelOf(node)}</span>
            <ChevronDown
              className={cn('h-4 w-4 shrink-0 transition-transform', !open && '-rotate-90')}
              aria-hidden
            />
          </button>
          {open ? (
            <div className="flex flex-col gap-0.5">
              {node.children.map((c) => (
                <MobileRow key={c.id} node={c} depth={depth + 1} />
              ))}
            </div>
          ) : null}
        </div>
      )
    }

    return null
  }

  /** Masaüstü: tek satır (yaprak veya flyout tetikleyici) */
  function DesktopItemRow({
    node,
    collapsedOnly,
  }: {
    node: AdminMenuNode
    collapsedOnly: boolean
  }) {
    const Icon = menuFaToLucide(node.icon)
    const triggerRef = useRef<HTMLButtonElement>(null)
    const hasKids = node.children.length > 0
    const flyoutOpen = flyout?.id === node.id
    const activeBranch = branchContainsActiveRoute(node, pathname, search, allMenuPathnames)

    if (!hasKids && node.path) {
      const active = pathMatches(pathname, search, node.path, allMenuPathnames)
      return (
        <NavLink
          to={menuPathToTo(node.path)}
          end
          title={collapsedOnly ? labelOf(node) : undefined}
          onClick={() => onNavigate()}
          aria-current={active ? 'page' : undefined}
          className={leafRowClass(active, collapsedOnly)}
        >
          <Icon className="h-4 w-4 shrink-0" aria-hidden />
          {!collapsedOnly ? (
            <span className="min-w-0 flex-1 truncate">{labelOf(node)}</span>
          ) : null}
        </NavLink>
      )
    }

    if (hasKids) {
      return (
        <>
          <button
            ref={triggerRef}
            type="button"
            data-admin-nav-flyout-trigger="1"
            title={collapsedOnly ? labelOf(node) : undefined}
            aria-expanded={flyoutOpen}
            onMouseEnter={() => {
              if (triggerRef.current) openFlyout(node, triggerRef.current)
            }}
            onMouseLeave={scheduleCloseFlyout}
            onClick={() => {
              if (flyoutOpen) {
                setFlyout(null)
              } else if (triggerRef.current) {
                openFlyout(node, triggerRef.current)
              }
            }}
            className={branchRowClass(activeBranch, collapsedOnly)}
          >
            <Icon className="h-4 w-4 shrink-0" aria-hidden />
            {!collapsedOnly ? (
              <>
                <span className="min-w-0 flex-1 truncate">{labelOf(node)}</span>
                <ChevronRight className="h-4 w-4 shrink-0 opacity-70" aria-hidden />
              </>
            ) : null}
          </button>
        </>
      )
    }

    return null
  }

  const flyoutPortal =
    flyout && isDesktopLayout ? (
      createPortal(
        <div
          ref={flyoutPanelRef}
          className="fixed z-[100] min-w-[260px] max-w-[min(100vw-2rem,320px)] overflow-y-auto overscroll-contain rounded-xl border border-border/80 bg-card py-2 shadow-strong animate-in fade-in zoom-in-95 slide-in-from-left-2 duration-150 dark:border-gray-700 dark:bg-secondaryDark"
          style={{
            top: flyoutStyle?.top ?? Math.max(12, flyout.rect.top - 4),
            left: flyoutStyle?.left ?? flyout.rect.right + 8,
            maxHeight: flyoutStyle?.maxHeight ?? 'calc(100vh - 24px)',
          }}
          onMouseEnter={clearCloseTimer}
          onMouseLeave={scheduleCloseFlyout}
        >
          <FlyoutMenuBody
            nodes={flyout.node.children}
            pathname={pathname}
            search={search}
            allMenuPathnames={allMenuPathnames}
            labelOf={labelOf}
            onPick={() => {
              setFlyout(null)
              onNavigate()
            }}
          />
        </div>,
        document.body,
      )
    ) : null

  /* ─── Mobil (drawer): bölüm + accordion (flyout yok) ─── */
  if (!isDesktopLayout) {
    return (
      <div className="flex flex-col gap-1">
        {filteredRoots.map((section) => (
          <div key={section.id}>
            <p className="mb-1.5 mt-4 px-3 text-[10px] font-bold uppercase tracking-[0.08em] text-muted-foreground/80 first:mt-1">
              {labelOf(section)}
            </p>
            <div className="flex flex-col gap-0.5">
              {section.children.map((item) => (
                <MobileRow key={item.id} node={item} depth={0} />
              ))}
            </div>
          </div>
        ))}
      </div>
    )
  }

  /* ─── Masaüstü daraltılmış: ikon + sağa flyout ─── */
  if (collapsed) {
    return (
      <div className="flex flex-col gap-1">
        {filteredRoots.map((section) => (
          <div key={section.id}>
            <p className="sr-only">{labelOf(section)}</p>
            <div className="flex flex-col gap-0.5">
              {section.children.map((item) => (
                <DesktopItemRow key={item.id} node={item} collapsedOnly />
              ))}
            </div>
          </div>
        ))}
        {flyoutPortal}
      </div>
    )
  }

  /* ─── Masaüstü geniş: bölüm + flyout ─── */
  return (
    <div className="flex flex-col gap-1">
      {filteredRoots.map((section) => (
        <div key={section.id}>
          <p className="mb-1.5 mt-4 px-3 text-[10px] font-bold uppercase tracking-[0.08em] text-muted-foreground/90 first:mt-1">
            {labelOf(section)}
          </p>
          <div className="flex flex-col gap-0.5">
            {section.children.map((item) => (
              <DesktopItemRow key={item.id} node={item} collapsedOnly={false} />
            ))}
          </div>
        </div>
      ))}
      {flyoutPortal}
    </div>
  )
}
